<script setup lang="ts">
// A copy-paste block for /examples/booking. Quiet by design: the code is the
// content, so no syntax colouring, no chrome — just a readable monospace block
// and one honest copy button.
const props = defineProps<{
  code: string
  /** Shown as a small label above the block, e.g. "HTML". */
  label?: string
}>()

const copied = ref(false)
const failed = ref(false)
const preEl = ref<HTMLElement | null>(null)

/**
 * Long blocks open at their first COLLAPSED_LINES lines. A 35-line recipe
 * pushed the next example off the screen and read as a wall before it read as
 * code; the first dozen lines are the part a visitor actually pastes first.
 * Only worth doing when the block is meaningfully longer than the fold —
 * collapsing an 18-line block to 12 hides six lines behind a click.
 */
const COLLAPSE_OVER_LINES = 16
const COLLAPSED_LINES = 12
const expanded = ref(false)
const lines = computed(() => props.code.split('\n'))
const isCollapsible = computed(() => lines.value.length > COLLAPSE_OVER_LINES)
const isCollapsed = computed(() => isCollapsible.value && !expanded.value)
/** Presentation only — Copy always writes `props.code`, every line of it. */
const shownCode = computed(() => (isCollapsed.value ? lines.value.slice(0, COLLAPSED_LINES).join('\n') : props.code))
const hiddenLineCount = computed(() => lines.value.length - COLLAPSED_LINES)
/**
 * The manual-copy shortcut, named for the keyboard the visitor actually has.
 * Resolved at failure time rather than up front: it reads `navigator`, and this
 * component is server-rendered.
 */
const manualCopyLabel = ref('Press Ctrl+C')
let resetTimer: ReturnType<typeof setTimeout> | undefined

function isMacLike(): boolean {
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } }
  // userAgentData.platform where it exists, then the frozen `platform` string,
  // then the UA — any of the three names an Apple keyboard clearly enough.
  return /mac|iphone|ipad|ipod/i.test(nav.userAgentData?.platform || nav.platform || nav.userAgent || '')
}

/**
 * Select the whole block so ⌘C / Ctrl+C actually has something to copy. The
 * label alone was an instruction the visitor could not follow: nothing was
 * selected, so the shortcut copied whatever else was on the page.
 */
function selectBlock() {
  const node = preEl.value
  if (!node || typeof getSelection !== 'function') return
  try { getSelection()?.selectAllChildren(node) }
  catch { /* selection refused (an old engine, a locked-down embed) */ }
}

async function copy() {
  failed.value = false
  try {
    // clipboard.writeText needs a secure context and permission; when it is
    // refused, select the block so the visitor can still copy it by hand.
    await navigator.clipboard.writeText(props.code)
    copied.value = true
  }
  catch {
    failed.value = true
    copied.value = false
    manualCopyLabel.value = isMacLike() ? 'Press ⌘C' : 'Press Ctrl+C'
    // Hand-copying a collapsed block would copy the first twelve lines only.
    expanded.value = true
    await nextTick()
    selectBlock()
  }
  if (resetTimer) clearTimeout(resetTimer)
  resetTimer = setTimeout(() => {
    copied.value = false
    failed.value = false
  }, 2400)
}

onBeforeUnmount(() => {
  if (resetTimer) clearTimeout(resetTimer)
})
</script>

<template>
  <div class="code-block">
    <div class="code-block-bar">
      <span class="code-block-label">{{ label || 'HTML' }}</span>
      <button type="button" class="code-block-copy" :aria-label="`Copy the ${label || 'HTML'} snippet`" @click="copy">
        {{ copied ? 'Copied' : failed ? manualCopyLabel : 'Copy' }}
      </button>
    </div>
    <div class="code-block-body" :class="{ 'is-collapsed': isCollapsed }">
      <pre ref="preEl" class="code-block-pre"><code>{{ shownCode }}</code></pre>
    </div>
    <button
      v-if="isCollapsible"
      type="button"
      class="code-block-more"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      {{ expanded ? 'Show less' : `Show full code (${hiddenLineCount} more lines)` }}
    </button>
  </div>
</template>

<style scoped>
.code-block {
  border: 1px solid var(--color-base-300);
  border-radius: 10px;
  background: var(--color-base-100);
  overflow: hidden;
}

.code-block-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px 8px 14px;
  border-bottom: 1px solid var(--color-base-300);
  background: color-mix(in oklab, var(--color-base-200) 60%, transparent);
}

.code-block-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: color-mix(in oklab, var(--color-base-content) 52%, transparent);
}

.code-block-copy {
  border: 1px solid var(--color-base-300);
  border-radius: 7px;
  background: var(--color-base-100);
  color: color-mix(in oklab, var(--color-base-content) 78%, transparent);
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  cursor: pointer;
  transition: color 120ms ease, border-color 120ms ease;
}

.code-block-copy:hover {
  color: var(--color-base-content);
  border-color: color-mix(in oklab, var(--color-base-content) 34%, transparent);
}

.code-block-copy:focus-visible {
  outline: 2px solid color-mix(in oklab, var(--color-base-content) 40%, transparent);
  outline-offset: 2px;
}

/*
 * Long lines WRAP rather than scroll away. The iframe snippet's src and its
 * credit anchor run for hundreds of characters, and a horizontal scroller on a
 * block with no visible scrollbar just hid them. `overflow-wrap: anywhere`
 * breaks inside a URL, which nothing else would. Wrapping is presentation
 * only — the Copy button writes `props.code`, so it still copies the exact
 * bytes, unwrapped. overflow-x stays as the last resort for anything that
 * still cannot break.
 */
/* The fold: a short fade over the last collapsed line so the cut reads as
   "there is more" rather than as the end of the snippet. */
.code-block-body {
  position: relative;
}

.code-block-body.is-collapsed::after {
  content: "";
  position: absolute;
  inset: auto 0 0;
  height: 48px;
  pointer-events: none;
  background: linear-gradient(to bottom, transparent, var(--color-base-100));
}

.code-block-more {
  display: block;
  width: 100%;
  border: 0;
  border-top: 1px solid var(--color-base-300);
  background: color-mix(in oklab, var(--color-base-200) 60%, transparent);
  color: color-mix(in oklab, var(--color-base-content) 66%, transparent);
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 14px;
  text-align: left;
  cursor: pointer;
  transition: color 120ms ease;
}

.code-block-more:hover {
  color: var(--color-base-content);
}

.code-block-more:focus-visible {
  outline: 2px solid color-mix(in oklab, var(--color-base-content) 40%, transparent);
  outline-offset: -2px;
}

.code-block-pre {
  margin: 0;
  padding: 16px 18px;
  overflow-x: auto;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  font-size: 12.5px;
  line-height: 1.7;
  color: color-mix(in oklab, var(--color-base-content) 88%, transparent);
  text-align: left;
  tab-size: 2;
}
</style>
